import React, { useEffect, useState } from 'react';
import { investmentPlanApi } from '../services/investmentPlanApi.ts';
import { investmentAdvisoryApi, ScenarioInput } from '../services/investmentAdvisoryApi.ts';
import { useConnectivityStatus } from '../hooks/useConnectivityStatus.ts';
import { formatCurrency } from '../utils/dateUtils.ts';
import { parseNumberInput, parsePercentInput } from '../utils/numberUtils.ts';
import './InvestmentScenarios.css';

interface InvestmentPlan {
  id: number;
  name: string;
}

const InvestmentScenarios: React.FC = () => {
  const [plans, setPlans] = useState<InvestmentPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [scenarios, setScenarios] = useState<any[]>([]);
  const [comparison, setComparison] = useState<Record<string, any> | null>(null);
  const [form, setForm] = useState({
    plan_id: 0,
    name: '',
    return_adjustment: '',
    inflation_adjustment: '',
    retirement_age_adjustment: '',
    notes: '',
  });
  const [scenarioSaving, setScenarioSaving] = useState(false);
  const [comparing, setComparing] = useState(false);
  const { isOnline, isBackendOnline } = useConnectivityStatus();
  const backendAvailable = isOnline && isBackendOnline;

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    const response = await investmentPlanApi.listPlans();
    setPlans(response.data || []);
  };

  const loadScenarios = async (planId: number) => {
    const response = await investmentAdvisoryApi.listScenarios(planId);
    setScenarios(response.data || []);
  };

  const handleCreateScenario = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlanId) return;
    setScenarioSaving(true);
    try {
      const payload: ScenarioInput = {
      plan_id: selectedPlanId,
      name: form.name,
      return_adjustment: parsePercentInput(form.return_adjustment),
      inflation_adjustment: parsePercentInput(form.inflation_adjustment),
      retirement_age_adjustment: parseNumberInput(form.retirement_age_adjustment),
      notes: form.notes,
    };
      await investmentAdvisoryApi.createScenario(payload);
      await loadScenarios(selectedPlanId);
    } finally {
      setScenarioSaving(false);
    }
  };

  const handleCompare = async () => {
    if (!selectedPlanId) return;
    setComparing(true);
    try {
      const response = await investmentAdvisoryApi.compareScenarios(selectedPlanId);
      setComparison(response.data || null);
    } finally {
      setComparing(false);
    }
  };

  return (
    <div className="investment-scenarios module-name">
      <div className="page-header">
        <div>
          <h1>Cenários de Investimento</h1>
          <p>Compare cenários otimista, base e pessimista.</p>
        </div>
      </div>

      <div className="module-content">
        {!backendAvailable && (
          <div className="message error" style={{ marginBottom: '16px' }}>
            ⚠️ Comparação de cenários exige conexão com o backend. Cadastros locais continuam disponíveis.
          </div>
        )}
        <div className="scenario-selection">
          <label>Plano base</label>
          <select
            value={selectedPlanId ?? ''}
            onChange={(e) => {
              const planId = Number(e.target.value);
              setSelectedPlanId(planId);
              if (planId) {
                loadScenarios(planId);
              }
            }}
          >
            <option value="">Selecione um plano</option>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </select>
        </div>

        <form className="form-container scenario-form" onSubmit={handleCreateScenario}>
          <h3>Novo cenário</h3>
          <div className="form-group">
            <label>Nome</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Ajuste retorno</label>
              <input
              type="text"
                value={form.return_adjustment}
              onChange={(e) => setForm({ ...form, return_adjustment: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Ajuste inflação</label>
              <input
              type="text"
                value={form.inflation_adjustment}
              onChange={(e) => setForm({ ...form, inflation_adjustment: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Ajuste idade aposentadoria</label>
            <input
            type="text"
              value={form.retirement_age_adjustment}
            onChange={(e) => setForm({ ...form, retirement_age_adjustment: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Observações</label>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <div className="form-actions">
            <button className="primary-button" type="submit" disabled={!selectedPlanId || scenarioSaving}>
              {scenarioSaving ? 'Salvando...' : 'Salvar cenário'}
            </button>
          </div>
        </form>

        <div className="scenario-list">
          <h3>Cenários registrados</h3>
          {scenarios.map((scenario) => (
            <div className="card" key={scenario.id}>
              <strong>{scenario.name}</strong>
              <span>Retorno: {scenario.return_adjustment}</span>
              <span>Inflação: {scenario.inflation_adjustment}</span>
            </div>
          ))}
        </div>

        <button
          className="primary-button"
          type="button"
          onClick={handleCompare}
          disabled={!selectedPlanId || !backendAvailable || comparing}
        >
          {comparing ? 'Comparando...' : 'Comparar cenários'}
        </button>

        {comparison && (
          <div className="scenario-comparison">
            {Object.entries(comparison).map(([name, projection]) => {
              const points = projection.points || [];
              const last = points[points.length - 1];
              return (
                <div className="card" key={name}>
                  <strong>{name}</strong>
                  <span>Patrimônio final: {last ? formatCurrency(last.nominal_net_worth) : 'N/A'}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestmentScenarios;
